import React, { useRef, useState, useCallback } from 'react';
import { TweetContentDto, TweetStatus } from '../../api/bip-api/types';

interface PublishConfirmModalProps {
  tweet: TweetContentDto;
  isPublishing: boolean;
  onConfirm: (isPersonalTweet: boolean) => void;
  onCancel: () => void;
}

const PublishConfirmModal: React.FC<PublishConfirmModalProps> = ({
  tweet,
  isPublishing,
  onConfirm,
  onCancel,
}) => {
  const iconRef = useRef<HTMLDivElement>(null);
  const [iconTransform, setIconTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [isPersonalTweet, setIsPersonalTweet] = useState(false);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!iconRef.current) return;

    const rect = iconRef.current.getBoundingClientRect();
    const iconCenterX = rect.left + rect.width / 2;
    const iconCenterY = rect.top + rect.height / 2;

    const deltaX = e.clientX - iconCenterX;
    const deltaY = e.clientY - iconCenterY;

    // Calculate rotation (max 35 degrees)
    const maxRotation = 35;
    const maxDistance = 200;

    const rotateY = Math.max(-maxRotation, Math.min(maxRotation, (deltaX / maxDistance) * maxRotation));
    const rotateX = Math.max(-maxRotation, Math.min(maxRotation, -(deltaY / maxDistance) * maxRotation));

    setIconTransform({ rotateX, rotateY });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIconTransform({ rotateX: 0, rotateY: 0 });
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="bg-gray-800 rounded-xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-medium text-white">Confirm Publish</h3>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning */}
          <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-sm text-yellow-200">
                Once published, this tweet cannot be edited or deleted.
              </p>
            </div>
          </div>

          {/* Community Warning or Published Status */}
          {tweet.status === TweetStatus.PUBLISHED ? (
            <div className="mb-4 p-3 bg-green-900/30 border border-green-600 rounded-lg">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p className="text-sm text-green-200">
                  This tweet has been published
                </p>
              </div>
            </div>
          ) : (
            tweet.communityId && (
              <div className="mb-4 relative overflow-hidden rounded-lg">
                {/* Animated gradient border */}
                <div
                  className={`absolute inset-0 animate-[shimmer_2s_linear_infinite] transition-all duration-300 ${
                    isPersonalTweet
                      ? 'bg-gradient-to-r from-green-500 via-emerald-500 to-green-500'
                      : 'bg-gradient-to-r from-red-500 via-orange-500 to-red-500'
                  }`}
                  style={{
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 2s linear infinite',
                  }}
                />
                <div className="relative m-[2px] p-3 bg-gray-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    {/* Warning Icon - only show when not personal tweet */}
                    {!isPersonalTweet && (
                      <div className="flex-shrink-0" style={{ perspective: '500px' }}>
                        <div
                          ref={iconRef}
                          className="relative transition-transform duration-150 ease-out"
                          style={{
                            transformStyle: 'preserve-3d',
                            transform: `rotateX(${iconTransform.rotateX}deg) rotateY(${iconTransform.rotateY}deg)`,
                          }}
                        >
                          <div
                            className="absolute inset-0 bg-red-500 rounded-full blur-md"
                            style={{
                              opacity: 0.6 + Math.abs(iconTransform.rotateX + iconTransform.rotateY) / 100,
                            }}
                          />
                          <svg
                            className="relative w-8 h-8 text-red-400 drop-shadow-lg"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ filter: 'drop-shadow(0 0 8px rgba(248, 113, 113, 0.6))' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-bold transition-colors duration-300 ${isPersonalTweet ? 'text-green-300' : 'text-red-300'}`}>
                        {isPersonalTweet ? '개인 트윗에 발송합니다' : '배포전에 커뮤니티 가입 여부를 확인하세요!'}
                      </p>
                      {/* Community Link - only show when not personal tweet */}
                      {!isPersonalTweet && (
                        <a
                          href={`https://x.com/i/communities/${tweet.communityId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white text-sm font-medium rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/40"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                          Community 가입 확인
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                      {/* Personal Tweet Toggle */}
                      <div className={`mt-3 pt-3 border-t transition-colors duration-300 ${isPersonalTweet ? 'border-green-500/30' : 'border-red-500/30'}`}>
                        <label className="flex items-center justify-between cursor-pointer group">
                          <span className={`text-sm transition-colors duration-300 ${isPersonalTweet ? 'text-green-300 group-hover:text-green-100' : 'text-red-200 group-hover:text-white'}`}>
                            {isPersonalTweet ? 'Community에 발송하기' : '개인 트윗에 발송하기'}
                          </span>
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={isPersonalTweet}
                              onChange={(e) => setIsPersonalTweet(e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`w-11 h-6 rounded-full transition-all duration-300 ${isPersonalTweet ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gray-700'}`} />
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-all duration-300 ${isPersonalTweet ? 'translate-x-5' : 'translate-x-0'}`} />
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <style>{`
                  @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
                `}</style>
              </div>
            )
          )}

          {/* Preview */}
          <div className="space-y-3 max-h-60 overflow-y-auto">
            <p className="text-sm font-medium text-gray-300 mb-2">Preview:</p>
            {tweet.contents.map((content, index) => (
              <div
                key={index}
                className="p-3 bg-gray-900 rounded-lg border border-gray-700"
              >
                <div className="text-xs text-gray-500 mb-1">#{index + 1}</div>
                <p className="text-sm text-white whitespace-pre-wrap">
                  {content}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            disabled={isPublishing}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(isPersonalTweet)}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            disabled={isPublishing}
          >
            {isPublishing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Publishing...
              </>
            ) : (
              isPersonalTweet ? '개인 트윗에 발송하기' : 'Community에 발송하기'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishConfirmModal;
